"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect, useRef, useState } from "react";
export default function Historia() {
  const {
    ref: containerRef,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const mainImageRef = useRef<HTMLDivElement>(null);
  const detailImageRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const scrollProgress = Math.max(0, Math.min(1, (window.innerHeight - rect.top) / window.innerHeight));
        setScrollY(scrollProgress);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [containerRef]);
  useEffect(() => {
    if (mainImageRef.current) {
      const mainTransform = scrollY * 30;
      mainImageRef.current.style.transform = `translateY(${mainTransform}px)`;
    }
    if (detailImageRef.current) {
      const detailTransform = scrollY * 60;
      detailImageRef.current.style.transform = `translateY(${detailTransform}px)`;
    }
  }, [scrollY]);
  return <section id="historia" ref={containerRef} className="relative bg-background py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {}
          <div className="relative h-[600px] lg:h-[700px]">
            {}
            <div ref={mainImageRef} className={`absolute left-0 top-0 w-2/3 h-5/6 overflow-hidden rounded-lg shadow-strong transition-all duration-[var(--duration-slow)] ${isVisible ? "opacity-100" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: "0ms"
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1548683/img/lumiere_historia_main_portrait.png" alt="Modelo elegante en entorno europeo" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[var(--duration-slow)] ease-[var(--ease-smooth)]" onMouseEnter={e => {
              e.currentTarget.style.transform = "scale(1.02)";
              e.currentTarget.parentElement!.style.boxShadow = "0 12px 32px hsla(48, 80%, 50%, 0.15), 0 4px 12px rgba(0, 0, 0, 0.08)";
              e.currentTarget.parentElement!.style.borderColor = "hsla(48, 80%, 50%, 0.4)";
              e.currentTarget.parentElement!.style.borderWidth = "2px";
            }} onMouseLeave={e => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.parentElement!.style.boxShadow = "0 12px 32px rgb(0 0 0 / 0.12), 0 4px 12px rgb(0 0 0 / 0.08)";
              e.currentTarget.parentElement!.style.borderColor = "transparent";
              e.currentTarget.parentElement!.style.borderWidth = "0px";
            }} />
            </div>

            {}
            <div ref={detailImageRef} className={`absolute right-0 bottom-0 w-3/5 h-2/5 overflow-hidden rounded-lg shadow-strong transition-all duration-[var(--duration-slow)] ${isVisible ? "opacity-100" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: "200ms"
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1548683/img/lumiere_historia_fabric_detail.png" alt="Detalle de tejido premium" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[var(--duration-slow)] ease-[var(--ease-smooth)]" onMouseEnter={e => {
              e.currentTarget.style.transform = "scale(1.02)";
              e.currentTarget.parentElement!.style.boxShadow = "0 12px 32px hsla(48, 80%, 50%, 0.15), 0 4px 12px rgba(0, 0, 0, 0.08)";
              e.currentTarget.parentElement!.style.borderColor = "hsla(48, 80%, 50%, 0.4)";
              e.currentTarget.parentElement!.style.borderWidth = "2px";
            }} onMouseLeave={e => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.parentElement!.style.boxShadow = "0 12px 32px rgb(0 0 0 / 0.12), 0 4px 12px rgb(0 0 0 / 0.08)";
              e.currentTarget.parentElement!.style.borderColor = "transparent";
              e.currentTarget.parentElement!.style.borderWidth = "0px";
            }} />
            </div>
          </div>

          {}
          <div className="space-y-12">
            {}
            <div className={`transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8 blur-sm"}`} style={{
            transitionDelay: "100ms"
          }}>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tight text-foreground mb-6">
                Nuestra Historia
              </h2>
              <p className="text-muted-foreground text-base leading-relaxed">
                LUMIÈRE Boutique nace de una visión clara: crear un espacio donde la moda se convierte en expresión personal. Desde nuestros inicios, hemos seleccionado cuidadosamente cada pieza con un enfoque en calidad, diseño consciente y elegancia atemporal.
              </p>
            </div>

            {}
            <div className={`transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8 blur-sm"}`} style={{
            transitionDelay: "200ms"
          }}>
              <h3 className="font-display text-2xl lg:text-3xl tracking-tight text-foreground mb-6">
                Filosofía de Estilo
              </h3>
              <p className="text-muted-foreground text-base leading-relaxed mb-6">
                Creemos en la moda como una forma de arte personal. Cada colección que presentamos refleja nuestra pasión por piezas que trascienden las tendencias y se convierten en parte de tu identidad.
              </p>
              <p className="text-muted-foreground text-base leading-relaxed">
                Nuestro compromiso con la excelencia se manifiesta en cada detalle: desde la selección de tejidos premium hasta el servicio personalizado que ofrecemos a nuestras clientas.
              </p>
            </div>

            {}
            <div className={`grid grid-cols-2 gap-8 pt-8 border-t border-border transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8 blur-sm"}`} style={{
            transitionDelay: "300ms"
          }}>
              <div>
                <h4 className="font-display text-lg text-foreground mb-2">
                  Calidad
                </h4>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Selección curada de las mejores marcas y diseñadores
                </p>
              </div>
              <div>
                <h4 className="font-display text-lg text-foreground mb-2">
                  Elegancia
                </h4>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Piezas atemporales que definen tu estilo único
                </p>
              </div>
              <div>
                <h4 className="font-display text-lg text-foreground mb-2">
                  Sostenibilidad
                </h4>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Compromiso con la moda consciente y responsable
                </p>
              </div>
              <div>
                <h4 className="font-display text-lg text-foreground mb-2">
                  Experiencia
                </h4>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Atención personalizada en cada paso de tu compra.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}